import type { IconButtonProps } from "@mui/material/IconButton";

import { useState, useCallback } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Popover from "@mui/material/Popover";
import Divider from "@mui/material/Divider";
import MenuList from "@mui/material/MenuList";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuItem, { menuItemClasses } from "@mui/material/MenuItem";
import SettingsIcon from "@mui/icons-material/Settings";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useMsal } from "@azure/msal-react";
import {
  b2cPolicies,
  loginRequest,
  logoutRequest,
  protectedResources,
} from "../../config/authConfig";
import { useTheme } from "@mui/material";
import { LanguagePopover } from "./LanguagePopover";
import { ColorModeSwitch } from "../Navbar/Navbar.styles";
import { langs } from "../../helpers/selectOptions";
import { useTranslation } from "react-i18next";
import { useOrganizationContext } from "../ContextProviders/OrganizationContext";
import { CustomAccountInfo } from "../../types/customAccountInfo";

// ----------------------------------------------------------------------

export type SettingsPopoverProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function SettingsPopover({
  data = [],
  sx,
  ...other
}: SettingsPopoverProps) {
  const { instance } = useMsal();
  const theme = useTheme();
  const { i18n } = useTranslation();
  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(
    null
  );
  const { changeColorMode } = useOrganizationContext();
  const currentAccount = instance.getActiveAccount() as CustomAccountInfo
  const [openLanguagePopover, setOpenLanguagePopover] =
    useState<HTMLButtonElement | null>(null);

  const handleOpenLanguagePopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setOpenLanguagePopover(event.currentTarget);
    },
    []
  );
  const handleCloseLanguagePopover = useCallback(() => {
    setOpenLanguagePopover(null);
  }, []);
  const handleOpenPopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setOpenPopover(event.currentTarget);
    },
    []
  );
  const currentLang = langs.find((c) => c.value.includes(i18n.language));

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);
  const handleResetPassword = () => {
    instance
      .loginRedirect({
        authority: b2cPolicies.authorities.forgotPassword.authority,
        scopes: [...protectedResources.api.scopes],
      })
      .catch((error) => {
        console.error("Password reset failed:", error);
      });
  };

  // TODO dubplicate in LanguagePopover. Move it
  const renderFlag = (label?: string, icon?: string) => (
      <Box
        component="img"
        alt={label}
        src={icon}
        sx={{ width: 26, height: 20, borderRadius: 0.5, objectFit: 'cover' }}
      />
    );

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          p: "2px",
          width: 40,
          height: 40,
          background: (theme) =>
            `conic-gradient(${theme.palette.primary.light}, ${theme.palette.warning.light}, ${theme.palette.primary.light})`,
          ...sx,
        }}
        {...other}
      >
        <Avatar
          sx={{
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : null,
          }}
        >
          <SettingsIcon />
        </Avatar>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { width: 200 },
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {currentAccount.idTokenClaims.name}
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
             {currentAccount.idTokenClaims.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <MenuList
          disablePadding
          sx={{
            p: 1,
            gap: 0.5,
            display: "flex",
            flexDirection: "column",
            [`& .${menuItemClasses.root}`]: {
              px: 1,
              gap: 2,
              borderRadius: 0.75,
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
              [`&.${menuItemClasses.selected}`]: {
                color: "text.primary",
                bgcolor: "action.selected",
                fontWeight: "fontWeightSemiBold",
              },
            },
          }}
        >
          <MenuItem component="button" onClick={handleOpenLanguagePopover}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              Language
              {renderFlag(currentLang?.label, currentLang?.src)}
            </Box>
          </MenuItem>
          <LanguagePopover
            anchorElement={openLanguagePopover}
            handleClosePopover={handleCloseLanguagePopover}
          />

          <MenuItem onClick={changeColorMode}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              Color mode
              <ColorModeSwitch sx={{marginRight : -2}} checked={theme.palette.mode === "dark"} />
            </Box>
          </MenuItem>

          <MenuItem onClick={handleResetPassword}>
            Change password
            <LockResetIcon />
          </MenuItem>
          {/* CONTENT */}
        </MenuList>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Box sx={{ p: 1 }}>
          <Button
            onClick={() => {
              instance.logoutPopup(logoutRequest);
              localStorage.removeItem("workspaceId");
            }}
            fullWidth
            color="error"
            size="medium"
            variant="text"
          >
            Logout
          </Button>
        </Box>
      </Popover>
    </>
  );
}
